import {
  Menus_VisibilityEnum as MenusVisibilityEnum,
  useDoMenuItemQuery,
  useMenuItemDetailsQuery
} from 'src/apollo/onlineOrdering'
import { Channel } from 'src/public/js/siteUtilities'

import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'

import { useFulfillment } from 'public/components/online_ordering/FulfillmentContext'

import { useDisplayPageContext } from '../../DisplayPageContext'

// TODO In an ideal world the fallback logic would happen in the backend for both these hooks

export const useMenuItemDetailsWithFallback = (
  itemGuid: string | undefined,
  itemGroupGuid: string | undefined,
  itemSlug: string | undefined = undefined
) => {
  const ooClient = useOOClient()
  const { selectedLocation, isEcommMultiLocationEnabled } = useRestaurant()
  const { channelGuid, channel } = useDisplayPageContext()
  const { fulfillmentData } = useFulfillment()

  const restaurantGuid = selectedLocation?.externalId || ''
  const dateTime = fulfillmentData?.cartFulfillmentData.fulfillmentDateTime
  const viewOnlyMenuChannelGuid = selectedLocation?.viewOnlyMenuChannelGuid
  const includeStockAcrossLocations =
    channel === Channel.ECOMM && isEcommMultiLocationEnabled
  const shouldSkip =
    !restaurantGuid || ((!itemGuid || !itemGroupGuid) && !itemSlug)

  const primaryChannelGuid =
    channel === Channel.OO ? viewOnlyMenuChannelGuid : channelGuid

  const {
    data: primaryData,
    error: primaryError,
    loading: primaryLoading
  } = useMenuItemDetailsQuery({
    variables: {
      input: {
        itemGuid: itemGuid || '',
        itemGroupGuid: itemGroupGuid || '',
        slug: itemSlug,
        restaurantGuid,
        channelGuid: primaryChannelGuid,
        dateTime,
        includeStockAcrossLocations,
        respectAvailability: false,
        includeSlugs: channel === Channel.ECOMM
      },
      nestingLevel: 10
    },
    client: ooClient,
    skip: shouldSkip
  })

  // Check if primary query has an error or error message in the data
  const primaryHasError =
    !!primaryError ||
    (primaryData?.menuItemDetails && 'message' in primaryData.menuItemDetails)

  const shouldUseFallback = channel === Channel.OO && primaryHasError

  const {
    data: fallbackData,
    error: fallbackError,
    loading: fallbackLoading
  } = useMenuItemDetailsQuery({
    variables: {
      input: {
        itemGuid: itemGuid || '',
        itemGroupGuid: itemGroupGuid || '',
        slug: itemSlug,
        restaurantGuid,
        channelGuid: undefined,
        dateTime,
        includeStockAcrossLocations,
        respectAvailability: false,
        includeSlugs: channel === Channel.ECOMM
      },
      nestingLevel: 10
    },
    client: ooClient,
    skip: shouldSkip || !shouldUseFallback
  })
  const data = shouldUseFallback ? fallbackData : primaryData
  const error = shouldUseFallback ? fallbackError : primaryError
  const loading = shouldUseFallback ? fallbackLoading : primaryLoading

  return { data, error, loading }
}

export const useDoMenuItemWithFallback = (
  itemGuid: string | undefined,
  itemGroupGuid: string | undefined,
  itemSlug: string | undefined = undefined
) => {
  const ooClient = useOOClient()
  const { selectedLocation } = useRestaurant()
  const { channelGuid, channel } = useDisplayPageContext()
  const { fulfillmentData } = useFulfillment()

  const restaurantGuid = selectedLocation?.externalId || ''
  const dateTime = fulfillmentData?.cartFulfillmentData.fulfillmentDateTime
  const viewOnlyMenuChannelGuid = selectedLocation?.viewOnlyMenuChannelGuid
  const shouldSkip =
    !restaurantGuid || ((!itemGuid || !itemGroupGuid) && !itemSlug)
  const isOoChannel = channel === Channel.OO
  const primaryChannelGuid = isOoChannel ? viewOnlyMenuChannelGuid : channelGuid

  const {
    data: primaryData,
    error: primaryError,
    loading: primaryLoading
  } = useDoMenuItemQuery({
    variables: {
      input: {
        menuGroupGuid: itemGroupGuid || '',
        menuItemGuid: itemGuid || '',
        slug: itemSlug,
        restaurantGuid,
        // eslint-disable-next-line camelcase
        visibility: MenusVisibilityEnum.ToastOnlineOrdering,
        dateTime,
        channelGuid: primaryChannelGuid,
        respectAvailability: false
      }
    },
    client: ooClient,
    skip: shouldSkip
  })

  const {
    data: fallbackData,
    error: fallbackError,
    loading: fallbackLoading
  } = useDoMenuItemQuery({
    variables: {
      input: {
        menuGroupGuid: itemGroupGuid || '',
        menuItemGuid: itemGuid || '',
        slug: itemSlug,
        restaurantGuid,
        // eslint-disable-next-line camelcase
        visibility: MenusVisibilityEnum.ToastOnlineOrdering,
        dateTime,
        channelGuid: undefined,
        respectAvailability: false
      }
    },
    client: ooClient,
    skip: shouldSkip || !isOoChannel
  })

  const primaryHasError =
    !!primaryError ||
    (primaryData?.doMenus_findMenuItem &&
      'message' in primaryData.doMenus_findMenuItem)

  const fallbackHasError =
    !!fallbackError ||
    (fallbackData?.doMenus_findMenuItem &&
      'message' in fallbackData.doMenus_findMenuItem)

  // For Channel.OO: use VOM data if successful, otherwise fallback
  // For Channel.ECOMM: just use the first data
  const shouldUsePrimary = !isOoChannel || (!primaryHasError && !!primaryData)

  const data = shouldUsePrimary ? primaryData : fallbackData
  const error = shouldUsePrimary ? primaryError : fallbackError
  const loading = isOoChannel
    ? primaryLoading || fallbackLoading
    : primaryLoading

  const isVomOnly = isOoChannel && !primaryHasError && fallbackHasError

  return { data, error, loading, isVomOnly }
}
